import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  useCreateEmployeeDocument,
  useDeleteEmployeeDocument,
  useEmployeeDocumentActivity,
  useEmployeeDocuments,
  useEmployeeDocumentTypes,
  useUpdateEmployeeDocument,
} from "../api/employeeDocs";
import { EMPLOYEE_DOCUMENT_LOCATIONS } from "../types.employee_docs";
import type { EmployeeDocument } from "../types.employee_docs";

interface Props {
  userId: string;
  canManageDocTypes?: boolean;
  onOpenDocTypes?: () => void;
}

type SortKey = "document_type_name" | "title" | "date_signed" | "effective_from" | "effective_to" | "location_display" | "created_by_name" | "created_at";

function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pl-PL");
}

function formatDateTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString("pl-PL") + ", " + d.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
}

const VERB_LABELS: Record<string, string> = {
  EMPLOYEE_DOCUMENT_CREATED: "Dodano dokument",
  EMPLOYEE_DOCUMENT_UPDATED: "Zaktualizowano dokument",
  EMPLOYEE_DOCUMENT_DELETED: "Usunięto dokument",
  EXAMINATION_UPDATED:       "Zaktualizowano daty badań",
};

interface DocFormState {
  document_type: string;
  location: string;
  title: string;
  date_signed: string;
  effective_from: string;
  effective_to: string;
  notes: string;
}

const EMPTY_FORM: DocFormState = {
  document_type: "",
  location: "",
  title: "",
  date_signed: "",
  effective_from: "",
  effective_to: "",
  notes: "",
};

function DocFormModal({
  userId,
  editDoc,
  onClose,
}: {
  userId: string;
  editDoc: EmployeeDocument | null;
  onClose: () => void;
}) {
  const { data: types = [] } = useEmployeeDocumentTypes();
  const activeTypes = types.filter((t) => t.is_active);
  const createDoc = useCreateEmployeeDocument(userId);
  const updateDoc = useUpdateEmployeeDocument(userId);
  const isEdit = editDoc !== null;

  const [form, setForm] = useState<DocFormState>(
    editDoc
      ? {
          document_type: editDoc.document_type,
          location: editDoc.location,
          title: editDoc.title,
          date_signed: editDoc.date_signed ?? "",
          effective_from: editDoc.effective_from ?? "",
          effective_to: editDoc.effective_to ?? "",
          notes: editDoc.notes,
        }
      : EMPTY_FORM
  );

  const [error, setError] = useState<string | null>(null);

  function set(key: keyof DocFormState, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.document_type) { setError("Wybierz typ dokumentu."); return; }
    setError(null);

    const payload = {
      document_type: form.document_type,
      location:      form.location || "",
      title:         form.title,
      date_signed:   form.date_signed   || null,
      effective_from: form.effective_from || null,
      effective_to:   form.effective_to   || null,
      notes:         form.notes,
    };

    if (isEdit && editDoc) {
      updateDoc.mutate({ id: editDoc.id, ...payload }, { onSuccess: onClose });
    } else {
      createDoc.mutate(payload as Parameters<typeof createDoc.mutate>[0], { onSuccess: onClose });
    }
  }

  const isPending = createDoc.isPending || updateDoc.isPending;

  return (
    <Modal open onClose={onClose} title={isEdit ? "Edytuj dokument" : "Dodaj dokument"} size="md">
      <form onSubmit={handleSubmit} className="p-4 space-y-3 overflow-y-auto">
        <label className="block space-y-1">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">Typ dokumentu *</span>
          <select
            value={form.document_type}
            onChange={(e) => set("document_type", e.target.value)}
            className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
          >
            <option value="">Wybierz typ…</option>
            {activeTypes.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </label>

        <label className="block space-y-1">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">Tytuł / numer</span>
          <input
            type="text"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="np. Umowa o pracę 2025"
            className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
          />
        </label>

        <div className="grid grid-cols-3 gap-3">
          <label className="block space-y-1">
            <span className="text-xs font-medium text-[var(--color-text-secondary)]">Data podpisania</span>
            <DatePicker
              value={form.date_signed}
              onChange={(v) => set("date_signed", v)}
              className="border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-[var(--color-text-secondary)]">Obowiązuje od</span>
            <DatePicker
              value={form.effective_from}
              onChange={(v) => set("effective_from", v)}
              className="border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-[var(--color-text-secondary)]">Obowiązuje do</span>
            <DatePicker
              value={form.effective_to}
              onChange={(v) => set("effective_to", v)}
              className="border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
            />
          </label>
        </div>

        <label className="block space-y-1">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">Lokalizacja oryginału</span>
          <select
            value={form.location}
            onChange={(e) => set("location", e.target.value)}
            className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
          >
            <option value="">— brak —</option>
            {EMPLOYEE_DOCUMENT_LOCATIONS.map((loc) => (
              <option key={loc.value} value={loc.value}>{loc.label}</option>
            ))}
          </select>
        </label>

        <label className="block space-y-1">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">Uwagi</span>
          <textarea
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            rows={2}
            className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] resize-none"
          />
        </label>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex gap-2 pt-1">
          <Button type="submit" size="sm" disabled={isPending}>
            {isPending ? "Zapisywanie…" : isEdit ? "Zapisz zmiany" : "Dodaj dokument"}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function EmployeeDocumentsPanel({ userId, canManageDocTypes, onOpenDocTypes }: Props) {
  const { data: docs = [], isLoading } = useEmployeeDocuments(userId);
  const { data: activity = [] } = useEmployeeDocumentActivity(userId);
  const deleteDoc = useDeleteEmployeeDocument(userId);

  const [showModal, setShowModal] = useState(false);
  const [editDoc, setEditDoc] = useState<EmployeeDocument | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortAsc, setSortAsc] = useState(false);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortAsc((a) => !a);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  }

  function SortTh({ col, label }: { col: SortKey; label: string }) {
    const active = sortKey === col;
    return (
      <th
        onClick={() => toggleSort(col)}
        className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide cursor-pointer select-none hover:text-[var(--color-text-primary)]"
      >
        {label}
        {active && <span className="ml-1">{sortAsc ? "↑" : "↓"}</span>}
      </th>
    );
  }

  const sorted = [...docs].sort((a, b) => {
    const av = (a[sortKey as keyof EmployeeDocument] ?? "") as string;
    const bv = (b[sortKey as keyof EmployeeDocument] ?? "") as string;
    return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  function handleDelete(docId: string) {
    deleteDoc.mutate(docId, { onSuccess: () => setDeleteConfirm(null) });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
          Dokumenty pracownika
        </h4>
        <div className="flex items-center gap-2">
          {canManageDocTypes && onOpenDocTypes && (
            <Button size="sm" variant="ghost" onClick={onOpenDocTypes}>
              Typy dokumentów
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => { setEditDoc(null); setShowModal(true); }}
          >
            + Dodaj dokument
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-[var(--color-text-tertiary)]">Ładowanie…</div>
      ) : docs.length === 0 ? (
        <div className="text-sm text-[var(--color-text-tertiary)] py-4 text-center">
          Brak dokumentów dla tego pracownika.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg-elevated)] border-b border-[var(--color-border)]">
              <tr>
                <SortTh col="document_type_name" label="Typ" />
                <SortTh col="title" label="Tytuł" />
                <SortTh col="date_signed" label="Podpisano" />
                <SortTh col="effective_from" label="Od" />
                <SortTh col="effective_to" label="Do" />
                <SortTh col="location_display" label="Lokalizacja oryginału" />
                <SortTh col="created_by_name" label="Dodał" />
                <SortTh col="created_at" label="Data dodania" />
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((doc) => (
                <tr
                  key={doc.id}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)] transition-colors"
                >
                  <td className="px-3 py-2 font-medium">{doc.document_type_name}</td>
                  <td className="px-3 py-2 text-[var(--color-text-secondary)]">{doc.title || "—"}</td>
                  <td className="px-3 py-2 text-[var(--color-text-secondary)]">{formatDate(doc.date_signed)}</td>
                  <td className="px-3 py-2 text-[var(--color-text-secondary)]">{formatDate(doc.effective_from)}</td>
                  <td className="px-3 py-2 text-[var(--color-text-secondary)]">{formatDate(doc.effective_to)}</td>
                  <td className="px-3 py-2 text-[var(--color-text-secondary)]">{doc.location_display || "—"}</td>
                  <td className="px-3 py-2 text-[var(--color-text-secondary)]">{doc.created_by_name || "—"}</td>
                  <td className="px-3 py-2 text-[var(--color-text-secondary)] whitespace-nowrap">{formatDate(doc.created_at)}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      {doc.file_url && (
                        <a
                          href={doc.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-[var(--color-primary)] hover:underline"
                        >
                          Plik
                        </a>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setEditDoc(doc); setShowModal(true); }}
                      >
                        Edytuj
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => setDeleteConfirm(doc.id)}
                      >
                        Usuń
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Activity timeline */}
      {activity.length > 0 && (
        <div className="space-y-2">
          <h5 className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
            Historia zmian
          </h5>
          <div className="space-y-2">
            {activity.map((entry) => {
              const changes = entry.metadata?.changes as { field: string; old: string; new: string }[] | undefined;
              return (
                <div key={entry.id} className="flex items-start gap-2 text-sm">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[var(--color-primary)] flex-shrink-0" />
                  <div className="min-w-0">
                    <div>
                      <span className="text-[var(--color-text-tertiary)] text-xs mr-2">
                        {formatDateTime(entry.timestamp)}
                      </span>
                      <span className="font-medium text-[var(--color-text-secondary)] mr-1">
                        {entry.actor_name}
                      </span>
                      <span className="text-[var(--color-text-secondary)]">
                        {VERB_LABELS[entry.verb] ?? entry.verb}
                      </span>
                      {entry.description && (
                        <span className="text-[var(--color-text-tertiary)]">: {entry.description}</span>
                      )}
                    </div>
                    {changes && changes.length > 0 && (
                      <ul className="mt-1 space-y-0.5 pl-0">
                        {changes.map((c, i) => (
                          <li key={i} className="text-xs text-[var(--color-text-tertiary)] font-mono">
                            <span className="text-[var(--color-text-secondary)]">{c.field}:</span>{" "}
                            <span className="line-through opacity-60">{c.old || "—"}</span>
                            {" → "}
                            <span className="text-[var(--color-text-primary)]">{c.new || "—"}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add/Edit modal */}
      {showModal && (
        <DocFormModal
          userId={userId}
          editDoc={editDoc}
          onClose={() => { setShowModal(false); setEditDoc(null); }}
        />
      )}

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <Modal open onClose={() => setDeleteConfirm(null)} title="Usuń dokument" size="sm">
          <div className="p-4 space-y-4">
            <p className="text-sm text-[var(--color-text-secondary)]">
              Czy na pewno chcesz usunąć ten dokument? Operacja jest nieodwracalna.
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="danger"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleteDoc.isPending}
              >
                {deleteDoc.isPending ? "Usuwanie…" : "Usuń"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(null)}>
                Anuluj
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
